import './Pdc.scss'

import { addNotification } from 'components/Notifications'
import ShareLinkCard from 'components/ShareLinkCard'
import ViewportOverlay from 'components/ViewportOverlay'
import { PdcStateItem, Position } from 'data/model/pdc'
import { RootState } from 'data/reducer'
import { fetchPdcItem, setError, setPdcItem } from 'data/reducer/pdc'
import { useAppDispatch, useAppSelector } from 'data/store/hooks'
import { MapType } from 'lib/directions'
import { i18n } from 'lib/i18n'
import { createDeepLink, Location, Place } from 'lib/place'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import CollectionsModal from './CollectionsModal'
import EditPdcCard from './EditPdcCard'
import PdcCard, { PdcCardItem } from './PdcCard'

export interface PdcProps {
  id?: string | Position
  onActionClick?: (event: PdcEvent) => void
  actionLabel?: string
  onError?: (error: string) => void
  onCloseButtonClick?: () => void
  data?: PdcCardData
}

export interface PdcCardData {
  deepLinkZoomLevel?: number
  deepLinkMapType?: MapType
}

export interface PdcEvent {
  id: string
  lat: number
  lng: number
  title: string
}

export default function Pdc({
  id,
  onError,
  onActionClick,
  actionLabel,
  onCloseButtonClick,
  data,
}: PdcProps): JSX.Element | null {
  const dispatch = useAppDispatch()
  const [isEditModeOpen, setIsEditModeOpen] = useState<boolean>(false)
  const [showShareModal, setShowShareModal] = useState<boolean>(false)
  const [addToCollectionModalPosition, setAddToCollectionModalPosition] = useState<null | {
    x: number
    y: number
  }>(null)
  const pdcItem = useAppSelector((state: RootState) => state.pdc.pdcItem)
  const user = useAppSelector((state) => state.account.user)
  const error = useAppSelector((state) => state.pdc.error)
  const pdcCardItem = useMemo(
    () => (pdcItem !== undefined ? fromPdcStateItemToPdcCardItem(pdcItem) : null),
    [pdcItem]
  )

  const handleOnFavorite = useCallback((position: { x: number; y: number }) => {
    setAddToCollectionModalPosition(position)
  }, [])

  const deepLinkUrl = useMemo(() => {
    if (pdcItem !== undefined && pdcItem.id !== undefined && pdcItem.position !== undefined) {
      const deepLinkData: Place | Location = pdcItem.id.startsWith('here:pds:place:')
        ? ({
            category:
              pdcItem.primaryCategory !== undefined
                ? encodeURIComponent(pdcItem.primaryCategory.name)
                : '',
            id: pdcItem.id,
            phone:
              pdcItem.contacts !== undefined && pdcItem.contacts.phone !== undefined
                ? pdcItem.contacts.phone
                : '',
            position: pdcItem.position,
            title: pdcItem.title,
          } as Place)
        : ({
            customTitle: pdcItem.title,
            position: pdcItem.position,
          } as Location)
      return createDeepLink(deepLinkData, data?.deepLinkZoomLevel, data?.deepLinkMapType)
    }

    return ''
  }, [data?.deepLinkMapType, data?.deepLinkZoomLevel, pdcItem])

  const handleCloseButton = useCallback(() => {
    if (onCloseButtonClick !== undefined) {
      onCloseButtonClick()
    }
  }, [onCloseButtonClick])

  const handleOnActionClick = useCallback(() => {
    if (
      onActionClick !== undefined &&
      pdcItem?.position?.lat !== undefined &&
      pdcItem?.position.lng !== undefined
    ) {
      onActionClick({
        id: pdcItem.id !== undefined ? pdcItem.id : crypto.randomUUID(),
        lat: pdcItem?.position.lat,
        lng: pdcItem?.position.lng,
        title: pdcItem.title,
      })
    }
  }, [onActionClick, pdcItem])

  const handleShareButtonClick = useCallback(() => {
    setShowShareModal(true)
  }, [])

  const handleLinkShare = useCallback(() => {
    dispatch(
      addNotification({
        content: i18n('notifications.clipboard-success'),
        duration: 5,
        inline: true,
        variant: 'confirmation',
      })
    )
  }, [dispatch])

  useEffect(() => {
    if (onError !== undefined && error !== undefined) {
      onError(error)
    }

    return () => {
      dispatch(setError(undefined))
    }
  }, [dispatch, error, onError])

  useEffect(() => {
    const isTypeOfAt = typeof id === 'object' && id.lat !== undefined && id.lng !== undefined
    const isTypeOfString = typeof id === 'string' && id !== undefined
    const decodedId = isTypeOfString ? decodeURIComponent(id) : undefined
    const at = isTypeOfAt ? id : undefined

    if (decodedId !== undefined || at !== undefined) {
      dispatch(fetchPdcItem(decodedId, at))
    } else {
      dispatch(setError('invalid input'))
    }

    return () => {
      dispatch(setPdcItem(undefined))
    }
  }, [dispatch, id])

  return (
    <>
      {pdcCardItem && (
        <div className="Pdc Map__panel Map__panel--top Map__panel--left Map__card">
          {user && isEditModeOpen ? (
            <EditPdcCard
              onClose={() => setIsEditModeOpen(false)}
              pdcItem={pdcCardItem}
              user={user}
            />
          ) : (
            <PdcCard
              item={pdcCardItem}
              onCloseButtonClick={handleCloseButton}
              onActionClick={handleOnActionClick}
              actionLabel={actionLabel}
              onSuggestEditButtonClick={() => setIsEditModeOpen(true)}
              onShare={handleShareButtonClick}
              onFavorite={handleOnFavorite}
            />
          )}
          {showShareModal && (
            <ViewportOverlay onClick={() => setShowShareModal(false)}>
              <ShareLinkCard
                className="Pdc__share-route-card"
                url={deepLinkUrl}
                onShare={handleLinkShare}
              />
            </ViewportOverlay>
          )}
        </div>
      )}
      {addToCollectionModalPosition !== null && pdcItem?.id !== undefined && (
        <CollectionsModal
          position={{ left: addToCollectionModalPosition.x, top: addToCollectionModalPosition.y }}
          pdcItem={pdcItem as Place}
          onClose={() => {
            setAddToCollectionModalPosition(null)
          }}
        />
      )}
    </>
  )
}

const fromPdcStateItemToPdcCardItem = (pdcStateItem: PdcStateItem): PdcCardItem => {
  return {
    addressLabel: pdcStateItem.addressLabel,
    averageRating: pdcStateItem.averageRating,
    contacts: pdcStateItem.contacts,
    distanceLabel: pdcStateItem?.distanceLabel,
    id: pdcStateItem.id,
    media: pdcStateItem.media,
    openingHours: pdcStateItem.openingHours,
    position: pdcStateItem.position,
    primaryCategory: pdcStateItem.primaryCategory,
    ratingLink: pdcStateItem.ratingLink,
    resultType: pdcStateItem.resultType,
    reviewCount: pdcStateItem.reviewCount,
    title: pdcStateItem.title,
  }
}
